import { useEffect, useMemo, useRef, useState } from "react";
import type { Hist } from "../hooks/useHistPrecio";
import { num } from "../lib/formato";
import { Giro } from "./Modal";

/** Ventanas del gráfico. `d` = días hacia atrás desde ahora. */
const RANGOS = [
  { k: "2D", d: 2 },
  { k: "7D", d: 7 },
  { k: "15D", d: 15 },
  { k: "30D", d: 30 },
] as const;

/** Eficiencia de capital de un rango [pa,pb] frente a rango completo (Uniswap V3).
 *  Exportada porque la ficha de una posición viva proyecta lo MISMO que este gráfico de creación
 *  (ver `ProyeccionMetrica` en DetalleVault): las dos cifras tienen que salir de la misma cuenta. */
export function concentracion(pa: number, pb: number) {
  const lo = Math.min(pa, pb), hi = Math.max(pa, pb);
  if (!(lo > 0 && hi > lo)) return 1;
  return 1 / (1 - Math.pow(lo / hi, 0.25));
}
// referencia: una posición ±10% rinde el `aprPct` del pool; de ahí se escala por ancho.
export const E_REF = concentracion(0.9, 1.1);

// El viewBox se hace ≈ al ancho REAL del lienzo (escala ≈1), para que el texto no encoja al bajar de
// tamaño: en móvil un viewBox fijo de 720 se escalaba a ~0,44 y las cifras quedaban en ~5px ilegibles.
const H = 220, PL = 8, PT = 14, PB = 24;

export function GraficoRango({
  hist, cargando, invVista, unidad, token0, token1,
  rawLo, rawHi, rawPx, aprBase, inv, tokenSym,
}: {
  hist: Hist | null;
  cargando: boolean;
  /** orientación que usa el resto del paso (estable como cotización). */
  invVista: boolean;
  unidad: string;
  token0: string;
  token1: string;
  /** rango y precio SIEMPRE en price0in1 (token1 por token0); aquí se orienta. */
  rawLo: number | null;
  rawHi: number | null;
  rawPx: number | null;
  aprBase: number;
  inv: number;
  tokenSym: string;
}) {
  const [dias, setDias] = useState<number>(7);
  const [inv0, setInv0] = useState<boolean>(invVista);

  // Ancho real del lienzo → viewBox, para que el gráfico escale sin achicar el texto.
  const lienzo = useRef<HTMLDivElement>(null);
  const [W, setW] = useState(680);
  useEffect(() => {
    const el = lienzo.current;
    if (!el) return;
    const medir = () => setW(Math.max(300, Math.min(760, Math.round(el.clientWidth || 680))));
    medir();
    const ro = new ResizeObserver(medir);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const PR = W < 460 ? 66 : 92;   // menos hueco derecho en pantallas estrechas

  const disp = (x: number) => (inv0 ? 1 / x : x);
  // par mostrado: T1/T0 (precio de T0 en T1) o T0/T1 si se invierte
  const parTxt = inv0 ? `${token1}/${token0}` : `${token0}/${token1}`;
  const unidadTxt = inv0 === invVista ? unidad : `${inv0 ? token1 : token0}`;

  const g = useMemo(() => {
    if (!hist) return null;
    const ahora = Math.floor(Date.now() / 1000);
    const desde = ahora - dias * 86400;
    const velas = hist.velas.filter((v) => v.t >= desde);
    if (velas.length < 2) return null;

    const pts = velas.map((v) => ({ t: v.t, y: disp(v.c) }));
    const lineas: number[] = [];
    if (rawLo != null) lineas.push(disp(rawLo));
    if (rawHi != null) lineas.push(disp(rawHi));
    const pxD = rawPx != null ? disp(rawPx) : null;

    const ys = [...pts.map((p) => p.y), ...lineas, ...(pxD != null ? [pxD] : [])];
    let ymin = Math.min(...ys), ymax = Math.max(...ys);
    const pad = (ymax - ymin || ymax || 1) * 0.08;
    ymin -= pad; ymax += pad;
    const t0 = pts[0]!.t, t1 = pts[pts.length - 1]!.t;
    const sx = (t: number) => PL + ((t - t0) / Math.max(1, t1 - t0)) * (W - PL - PR);
    const sy = (y: number) => PT + (1 - (y - ymin) / Math.max(1e-12, ymax - ymin)) * (H - PT - PB);

    const d = pts.map((p, i) => `${i ? "L" : "M"}${sx(p.t).toFixed(1)},${sy(p.y).toFixed(1)}`).join(" ");
    const area = `${d} L${sx(t1).toFixed(1)},${(H - PB).toFixed(1)} L${sx(t0).toFixed(1)},${(H - PB).toFixed(1)} Z`;

    // etiquetas de fecha (3)
    const fechas = [0, 0.5, 1].map((f) => {
      const t = t0 + f * (t1 - t0);
      const dt = new Date(t * 1000);
      return { x: sx(t), txt: `${dt.getDate()}/${dt.getMonth() + 1}` };
    });

    return { pts, d, area, sx, sy, ymin, ymax, t0, t1, pxD, fechas };
  }, [hist, dias, inv0, rawLo, rawHi, rawPx, W, PR]);

  // ------- APR estimado por ancho de rango + ganancia por lo invertido
  const apr = useMemo(() => {
    if (!aprBase || rawLo == null || rawHi == null) return null;
    const est = aprBase * (concentracion(rawLo, rawHi) / E_REF);
    const anual = inv * (est / 100);
    return { est, dia: anual / 365, mes: anual / 12, ano: anual };
  }, [aprBase, rawLo, rawHi, inv]);

  const loD = rawLo != null ? disp(rawLo) : null;
  const hiD = rawHi != null ? disp(rawHi) : null;
  const pxD = rawPx != null ? disp(rawPx) : null;
  const pctDe = (v: number | null) => (v != null && pxD ? (v / pxD - 1) * 100 : null);
  const fmtPct = (v: number | null) =>
    v == null ? "" : `${v >= 0 ? "+" : ""}${v.toFixed(Math.abs(v) < 10 ? 1 : 0)}%`;

  return (
    <div className="grafico-rango">
      <div className="gr-barra">
        <div className="gr-rangos">
          {RANGOS.map((r) => (
            <button key={r.k} className={`gr-chip ${dias === r.d ? "activo" : ""}`}
              onClick={() => setDias(r.d)}>{r.k}</button>
          ))}
        </div>
        <button className="gr-par" onClick={() => setInv0((v) => !v)} title="Invertir el par">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M7 4v13M4 14l3 3 3-3M17 20V7M14 10l3-3 3 3" />
          </svg>
          {parTxt}
        </button>
      </div>

      <div className="gr-lienzo" ref={lienzo}>
        {cargando ? (
          <div className="gr-vacio"><Giro texto="cargando histórico…" /></div>
        ) : !g ? (
          <div className="gr-vacio">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M3 3v18h18" /><path d="M7 14l3-3 3 3 4-5" />
            </svg>
            <p>Aún no hay histórico de precio para esta piscina.</p>
            <span>El robot lo reconstruye de los swaps; vuelve en unos minutos.</span>
          </div>
        ) : (
          <svg viewBox={`0 0 ${W} ${H}`} className="gr-svg">
            <defs>
              <linearGradient id="grArea" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--baliza)" stopOpacity="0.22" />
                <stop offset="100%" stopColor="var(--baliza)" stopOpacity="0" />
              </linearGradient>
            </defs>

            {/* banda del rango activo */}
            {loD != null && hiD != null && (
              <rect x={PL} y={g.sy(Math.max(loD, hiD))} width={W - PL - PR}
                height={Math.abs(g.sy(Math.min(loD, hiD)) - g.sy(Math.max(loD, hiD)))}
                fill="var(--marea)" opacity="0.10" />
            )}

            {/* área + línea de precio */}
            <path d={g.area} fill="url(#grArea)" />
            <path d={g.d} fill="none" stroke="var(--baliza)" strokeWidth="2"
              vectorEffect="non-scaling-stroke" strokeLinejoin="round" strokeLinecap="round" />

            {/* fechas */}
            {g.fechas.map((f, i) => (
              <text key={i} x={f.x} y={H - 7} className="gr-x" textAnchor={i === 0 ? "start" : i === 2 ? "end" : "middle"}>{f.txt}</text>
            ))}

            {/* líneas del rango, con precio y ±% a la derecha */}
            {[{ v: hiD, cls: "sube" }, { v: loD, cls: "baja" }].map((L, i) =>
              L.v == null ? null : (
                <g key={i} className={`gr-lim ${L.cls}`}>
                  <line x1={PL} y1={g.sy(L.v)} x2={W - PR} y2={g.sy(L.v)}
                    strokeDasharray="4 3" vectorEffect="non-scaling-stroke" />
                  <text x={W - PR + 6} y={g.sy(L.v) - 2} className="gr-lbl">
                    {num(L.v, L.v < 1 ? 5 : 2)}
                  </text>
                  <text x={W - PR + 6} y={g.sy(L.v) + 11} className="gr-lbl pct">
                    {fmtPct(pctDe(L.v))}
                  </text>
                </g>
              )
            )}

            {/* precio de ahora */}
            {pxD != null && (
              <g className="gr-ahora">
                <line x1={PL} y1={g.sy(pxD)} x2={W - PR} y2={g.sy(pxD)} vectorEffect="non-scaling-stroke" />
                <circle cx={W - PR} cy={g.sy(pxD)} r="3.2" />
                <text x={W - PR + 6} y={g.sy(pxD) + 3.5} className="gr-lbl ahora">{num(pxD, pxD < 1 ? 5 : 2)}</text>
              </g>
            )}
          </svg>
        )}
      </div>

      <p className="gr-nota">
        Precio de <strong>{parTxt}</strong> en {unidadTxt} · últimos {dias} días · reconstruido de los swaps del pool.
      </p>

      {apr && (
        <div className="gr-apr">
          <div className="gr-apr-cab">
            <span className="gr-apr-k">APR estimado</span>
            <span className="gr-apr-v">{num(apr.est, apr.est < 100 ? 1 : 0)}%</span>
          </div>
          <div className="gr-apr-gan">
            {inv > 0 ? (
              <>
                <div><span>al día</span><b>{num(apr.dia, 2)} {tokenSym}</b><i>+{num(apr.est / 365, 2)}%</i></div>
                <div><span>al mes</span><b>{num(apr.mes, 2)} {tokenSym}</b><i>+{num(apr.est / 12, 1)}%</i></div>
                <div><span>al año</span><b>{num(apr.ano, 2)} {tokenSym}</b><i>+{num(apr.est, 0)}%</i></div>
              </>
            ) : (
              <p className="gr-apr-nada">Escribe cuánto inviertes para ver la ganancia estimada.</p>
            )}
          </div>
          <p className="gr-apr-nota">
            Estimado <strong>si el precio se queda en el rango</strong>: a más estrecho, más APR, pero
            sale antes. No cuenta la pérdida por movimiento (impermanente).
          </p>
        </div>
      )}
    </div>
  );
}
